import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useDashboardEnforcement } from '../../hooks/useDashboard';

interface Props {
  filters: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    facilityTypeId?: string;
    geoLocationId?: string;
  };
}

const DONUT_COLORS = ['var(--nhip-red)', '#F59E0B', '#6B7280', '#3B82F6'];

function StatBox({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      className='flex-1 flex flex-col items-center justify-center py-4 px-3 rounded-xl'
      style={{ border: '1px solid var(--nhip-border)', backgroundColor: 'var(--nhip-surface)' }}
    >
      <span className='text-3xl font-bold tracking-tight' style={{ color }}>
        {value.toLocaleString()}
      </span>
      <span className='text-xs mt-1' style={{ color: 'var(--nhip-ink-muted)' }}>{label}</span>
    </div>
  );
}

function SkeletonStatBox() {
  return (
    <div
      className='flex-1 flex flex-col items-center justify-center py-4 px-3 rounded-xl animate-pulse gap-2'
      style={{ border: '1px solid var(--nhip-border)', backgroundColor: 'var(--nhip-surface)' }}
    >
      <div className='h-8 w-12 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
      <div className='h-3 w-16 rounded' style={{ backgroundColor: 'var(--nhip-border)' }} />
    </div>
  );
}

export default function EnforcementPanel({ filters }: Props) {
  const { data, isLoading } = useDashboardEnforcement(filters);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  const decisions = raw?.decisions ?? {};

  const permanentClosures: number = decisions.PERMANENT_CLOSING_OF_THE_FACILITY ?? 0;
  const temporaryClosures: number = decisions.TEMPORARY_CLOSING ?? 0;
  const closures = permanentClosures + temporaryClosures;
  const fines: number = decisions.FINES ?? 0;
  const warnings: number = decisions.WARNING ?? 0;
  const relocations: number = decisions.RELOCATION_OF_THE_HOUSEHOLD ?? 0;

  const donutData = [
    { name: 'Closures', value: closures },
    { name: 'Fines',    value: fines },
    { name: 'Warnings', value: warnings },
    ...(relocations > 0 ? [{ name: 'Relocations', value: relocations }] : []),
  ].filter(d => d.value > 0);

  const donutTotal = donutData.reduce((s, d) => s + d.value, 0);

  return (
    <div
      className='bg-white rounded-xl p-5'
      style={{ border: '1px solid var(--nhip-border)' }}
    >
      {/* Header */}
      <div className='flex items-start justify-between mb-4'>
        <div>
          <h2 className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>
            Enforcement this month
          </h2>
          {raw?.period && (
            <p className='text-xs mt-0.5' style={{ color: 'var(--nhip-ink-muted)' }}>
              Decisions issued in {raw.period}
            </p>
          )}
        </div>
        <button className='text-xs font-medium flex items-center gap-0.5' style={{ color: 'var(--nhip-green)' }}>
          View all <span>→</span>
        </button>
      </div>

      {/* Stat boxes */}
      <div className='flex gap-3 mb-5'>
        {isLoading ? (
          <>
            <SkeletonStatBox />
            <SkeletonStatBox />
            <SkeletonStatBox />
          </>
        ) : (
          <>
            <StatBox label='Closures' value={closures} color='var(--nhip-red)' />
            <StatBox label='Fines'    value={fines}    color='#F59E0B' />
            <StatBox label='Warnings' value={warnings} color='#3B82F6' />
          </>
        )}
      </div>

      {/* Decision distribution */}
      <p
        className='text-[10px] font-semibold uppercase tracking-wider mb-3'
        style={{ color: 'var(--nhip-ink-muted)' }}
      >
        Decision Distribution
      </p>

      {isLoading ? (
        <div className='flex justify-center py-6 animate-pulse'>
          <div className='w-28 h-28 rounded-full' style={{ backgroundColor: 'var(--nhip-border)' }} />
        </div>
      ) : donutTotal === 0 ? (
        <p className='text-xs text-center py-4' style={{ color: 'var(--nhip-ink-muted)' }}>No enforcement actions</p>
      ) : (
        <div className='flex flex-col items-center gap-3'>
          <div className='relative'>
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie
                  data={donutData}
                  cx='50%'
                  cy='50%'
                  innerRadius={48}
                  outerRadius={72}
                  dataKey='value'
                  strokeWidth={0}
                >
                  {donutData.map((_, i) => (
                    <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    border: '1px solid var(--nhip-border)',
                    borderRadius: 6,
                    fontSize: 11,
                    padding: '4px 8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className='absolute inset-0 flex flex-col items-center justify-center pointer-events-none'>
              <span className='text-lg font-bold' style={{ color: 'var(--nhip-ink)' }}>
                {donutTotal.toLocaleString()}
              </span>
              <span className='text-[10px]' style={{ color: 'var(--nhip-ink-muted)' }}>assessments</span>
            </div>
          </div>

          {/* Legend */}
          <div className='flex flex-wrap justify-center gap-x-4 gap-y-1'>
            {donutData.map((d, i) => (
              <div key={d.name} className='flex items-center gap-1.5'>
                <div className='w-2.5 h-2.5 rounded-full flex-shrink-0' style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                <span className='text-[11px]' style={{ color: 'var(--nhip-ink-muted)' }}>
                  {d.name} <span className='font-semibold' style={{ color: 'var(--nhip-ink)' }}>{d.value}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
